import Reference from "@/prototype/scene/geonodes/Reference.js";
import Geonode   from "@/prototype/scene/geonodes/Geonode.js";
import Point from "@/prototype/projection/Point.js";
import { DEGREES } from "@/prototype/projection/constants.js";
import { maxE, minE, maxN, minN, points_angle} from "@/prototype/projection/tools.js";

import global from "@/prototype/global.js";
const log = global.log.getLogger("f_mtac.scene.geonodes");
log.setLevel(log.levels.WARN);

const Geonodes = function(svg, objects) {
    var context = null;
    var geonode = {A: null, B: null};
    let center_x, center_y;
    let rotation = 0; // angle of the north with the vertical

    objects.layer_create("geolocation", "svg");
    svg.layer_insert("geolocation", "axis");
    svg.layer_hide("geolocation");

    const group = objects.layers.geolocation.group;
    const reference = new Reference(svg, objects);

    Object.defineProperties(this, {
        // no matter the width and height, remember the center
        image_width: {
            set: (value) => {center_x = value / 2;},
        },
        image_height: {
            set: (value) => {center_y = value / 2;},
        }
    });

    this.debug_disable = () => {
        reference.debug_disable();
    };

    this.get_by_node = (node) => {
        var type = node.dataset.geonodeName;
        return geonode[type];
    };

    /**
     * @brief get a geonode from its given type
     * @param {string} type , "A" or "B"
     * @returns the geonode
     */
    this.get_by_type = (type) => {
        return geonode[type];
    };

    this.update = (type, {X, Y, E, N}) => {
        if (geonode[type] === null) {
            geonode[type] = new Geonode(this, group, type);
            geonode[type].draw({x: X, y: Y, E, N});
        } else {
            geonode[type].move_to({x: X, y: Y});
            geonode[type].coordinates.E = E;
            geonode[type].coordinates.N = N;
        }
    };

    this.clean = () => {
        log.debug("Geonodes.clean");
        rotation = 0;
        context.projection.rotation = 0;
        this.remove("A");
        this.remove("B");
    };

    this.remove = (type) => {
        if (geonode[type] === null) {
            return;
        }
        geonode[type].handle.remove();
        geonode[type] = null;
    };

    /**
     * @brief Applies the geonode coordinates to compute the projection and to draw 
     */
    this.apply = async () => {
        log.debug("Geonodes.apply current rotation:", {rotation});

        // hide drawings during computation
        global.scene.map.visibility = "hidden";
        global.scene.map.rotation = 0.;
        try {

            // geonode A

            if (geonode.A === null) {
                context.projection.remove_point("A");
            } else {
                let {x, y} = geonode.A.position;
                let {E, N} = geonode.A.coordinates;

                if (rotation != 0) {
                    // if current rotation is not null, apply the opposite rotation to the node
                    // so as to restore the coordinates in a non-rotated system
                    let newA = rotate([x, y], [center_x,center_y], -1.0 * rotation);
                    x = newA[0];
                    y = newA[1];
                }
                context.projection.add_point("A", {X: x, Y: y, E, N});
            }

            // geonode B

            if (geonode.B === null) {
                context.projection.remove_point("B");
            } else {
                let {x, y} = geonode.B.position;
                let {E, N} = geonode.B.coordinates;
                if (rotation != 0) {
                    // if current rotation is not null, apply the opposite rotation to the node
                    // so as to restore the coordinates in a non-rotated system
                    let newB = rotate([x, y], [center_x,center_y], -1.0 * rotation);
                    x = newB[0];
                    y = newB[1];
                }
                context.projection.add_point("B", {X: x, Y: y, E, N});
            }

            // If both geonodes are defined -> the image is fully localized
            // FInd the rotation and rotate the image
            if (geonode.A !== null && geonode.B !== null) {
                log.debug("Geonodes.apply A and B not null");

                var { A, B } = context.projection;

                rotation = this.get_north_angle();
                log.debug("Geonodes.apply new rotation = ", rotation);

                // rotate arround the center of the image
                let newA = rotate([A.X, A.Y], [center_x,center_y], rotation);
                let newB = rotate([B.X, B.Y], [center_x,center_y], rotation);

                context.projection.add_point("A", {X: newA[0], Y: newA[1], E: A.E, N: A.N});
                context.projection.add_point("B", {X: newB[0], Y: newB[1], E: B.E, N: B.N});

                //reference.draw(context.projection);
                context.projection.rotation = rotation;

                global.scene.map.rotation = (-1. * rotation);

                await context.projection.save();   

                // compute grid settings with the new projection
                let common_data = await global.storage.selected_space.get_grid_common_data();
                if (common_data !== undefined) {
                    await global.scene.map.grid.init_image_from_common_grid();
                    global.scene.map.grid_save();
                    global.scene.map.grid.init_axis();
                }
                
                else {
                    // INITIATE COMMON GRID DATA (first desktop of the room)
                    await global.scene.map.grid.init_common_grid_from_image();
                    global.scene.map.grid_save();
                    global.scene.map.grid.is_extended = true;
                    global.scene.map.grid.init_axis();
                    //global.scene.map.grid.draw();
                }

                // display map scale
                global.scene.map.grid.frame.draw();
            }
            // if one geonode is null, no rotation and local grid
            else if (geonode.A === null || geonode.B === null) {
                log.debug("Geonodes.apply A or B is null");

                if (rotation != 0) {
                    // a geonode has been removed
                    // cancel the rotation and display the default grid
                    
                    if (geonode.A !== null) {
                        let {x, y} = geonode.A.position;
                        let {E, N} = geonode.A.coordinates;
    
                        // if current rotation is not null, apply the opposite rotation to the node
                        let newA = rotate([x, y], [center_x,center_y], -1.0 * rotation);
                        context.projection.add_point("A", {X: newA[0], Y: newA[1], E, N});
                    }
                    if (geonode.B !== null ) {
                        let {x, y} = geonode.B.position;
                        let {E, N} = geonode.B.coordinates;
    
                        // if current rotation is not null, apply the opposite rotation to the node
                        let newB = rotate([x, y], [center_x,center_y], -1.0 * rotation);
                        context.projection.add_point("B", {X: newB[0], Y: newB[1], E, N});
                    }

                    rotation = 0;
                    context.projection.rotation = 0;
                    global.scene.map.rotation = 0;
                }

                await context.projection.save();
                
                global.scene.map.grid.is_extended = false;
                global.scene.map.grid.reset_coordinates();
                global.scene.map.grid_save();
                global.scene.map.grid.init_axis();
                global.scene.map.grid.draw();
            }
            else {
                await context.projection.save();         
            }
            reference.draw(context.projection);
    
        } catch(err) {
            log.error("Geonodes.apply exception", err);
        }

        global.scene.map.visibility = "";
        
        log.debug("Geonodes.apply out");
    };

    /**
     * @brief return angle (in degrees) between the verticale and the north
     */
    this.get_north_angle = () => {
        var { A, B } = context.projection;
        const P = [];
        // TODO simplify, keep only NW and NE
        const NE = 0, SE = 1, NW = 3;
        for (let c = NE; c <= NW; c++) { // corners
            P[c] = new Point();
            P[c].EN = [(c <= SE) ? maxE(A, B) : minE(A, B), (c <= NE || c >= NW) ? maxN(A, B) : minN(A, B)];
            P[c].XY = context.projection.project(P[c].EN);
        }
        
        //log.debug(`Geonodes.get_north_angle ${P[NE].Y - P[NW].Y} / ${P[NE].X - P[NW].X}`);
        let angle = points_angle(P[NW], P[NE]) * DEGREES;
        log.debug("Geonodes.get_north_angle", {angle});
        return angle;
    };

    this.load = () => {
        var {A, B, rotation:rot} = context.projection;
        log.debug("Geonodes.load", {A, B, rot});
        if (A === null) {
            this.remove("A");
        } else {
            this.update("A", A);
        }
        if (B === null) {
            this.remove("B");
        } else {
            this.update("B", B);
        }
        rotation = rot;
        if (rotation != 0) {
            global.scene.map.rotation = (-1. * rotation);
        }

        // rotation that has been applied to the geonodes (and to the image)
        // for the north to be bottom -> up

        reference.draw(context.projection);
    };

    this.update_distance_style = () => {
        reference.update_distance_style();
    };

    this.change_context = async (new_context) => {
        var old_context = context;
        context = new_context;
        if (new_context === old_context) {
            return;
        }
        if (old_context !== null) {
            this.remove("A");
            this.remove("B");
        }
        if (new_context !== null) {
            this.load();
        }
    };

    /**
     * @brief Compute the rotation of a given point arround a given center
     * @param M coordinates [x,y] of the point to rotate
     * @param O coordinates [x,y] of the origin
     * @param angle, in degrees
     * @returns 
     */
    const rotate = function (M, O, angle) {
        var xM, yM, x, y;
        angle *= Math.PI / 180;
        xM = M[0] - O[0];
        yM = M[1] - O[1];
        x = xM * Math.cos (angle) + yM * Math.sin (angle) + O[0];
        y = - xM * Math.sin (angle) + yM * Math.cos (angle) + O[1];
        return [Math.round (x), Math.round (y)];
    };
};

export default Geonodes;
